/**
 * ExportDialog — Modal genérico de exportação de dados
 * -----------------------------------------------------------------------------
 * Reutilizável por qualquer módulo do sistema. Permite escolher:
 *   • Formato do arquivo (CSV ou XLS)
 *   • Colunas/campos que irão para o arquivo (agrupados, com busca)
 *   • Forma de entrega: baixar arquivo ou enviar para o e-mail do usuário
 *     (conforme as permissões do perfil — ver useMyExportPermissions)
 *
 * Toda exportação é registrada na trilha de auditoria (export_logs).
 *
 * Leiaute interno segue o padrão do painel lateral do Workflow
 * (SectionCard + CompactLabel), garantindo a mesma tipografia e densidade.
 *
 * @version 1.7.0
 * RELEASE: 2026-09-12 v1.7.0 - Suporte a fontes com colunas dinâmicas: quando a
 *          fonte implementa resolveColumns, os campos são carregados ao abrir
 *          o modal (ex.: listas importadas, cujos campos variam por arquivo).
 * RELEASE: 2026-09-07 v1.6.0 - Acima de 1.000 registros o download passa a ser
 *          gerado NO SERVIDOR (Edge Function com delivery='download'), evitando
 *          o corte silencioso da camada de API. Até 1.000 mantém o fluxo atual.
 * RELEASE: 2026-09-07 v1.5.0 - Formato e entrega passam a ser cartões de opção
 *          (ícone + título + explicação) no lugar do seletor segmentado, e os
 *          botões do rodapé seguem o padrão compacto dos modais de anotação.
 * RELEASE: 2026-09-07 v1.4.0 - A auditoria passa a gravar também o resumo
 *          legível dos filtros/ordenação (chave `_resumo`, via describeParams
 *          da fonte), para o detalhe da exportação não exibir nomes técnicos.
 * RELEASE: 2026-09-07 v1.3.0 - Resultado passa a ser exibido no cartão
 *          ExportResultToast (progresso, sucesso com "baixar novamente" e erro
 *          com "tentar novamente") e o XLSX sai como relatório de marca.
 * RELEASE: 2026-09-07 v1.2.0 - Informa no modal o total de registros que serão
 *          exportados (respeitando os filtros da tela), com aviso de teto e
 *          bloqueio quando não há registros.
 * RELEASE: 2026-09-07 v1.1.1 - Exibe no aviso a mensagem real devolvida pelo servidor.
 * RELEASE: 2026-09-07 v1.1.0 - Redesenho interno: cartões de seção no padrão do
 *          painel do Workflow, seletores segmentados de formato/entrega, busca
 *          de campos, contadores por grupo e avisos via InlineNotice.
 *          Nenhuma alteração de regra, consulta, permissão ou auditoria.
 * RELEASE: 2026-09-07 v1.0.0 - Criação inicial.
 */

import { useEffect, useMemo, useState } from 'react';
import { Download, FileSpreadsheet, FileText, Loader2, Mail, Search } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { InlineNotice } from '@/components/ui/inline-notice';
import { WorkflowDialogShell } from '@/components/workflow/WorkflowDialogShell';
import { SectionCard, CompactLabel } from '@/components/workflow/inspector/SectionCard';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useMyExportPermissions } from '@/hooks/useExportPermissions';
import { getExportSource } from '@/lib/export/exportRegistry';
import { buildExportBlob, triggerDownload } from '@/lib/export/writers';
import { useSystemSettings } from '@/hooks/useSystemSettings';
import { ExportResultToast, type ExportResultState } from './ExportResultToast';
import { EXPORT_ROW_LIMIT } from '@/lib/export/types';

/**
 * Teto seguro de registros processados no navegador.
 * Acima disso o arquivo é montado no servidor (a camada de API corta respostas
 * maiores que 1.000 linhas silenciosamente).
 */
const LIMITE_NAVEGADOR = 1000;
import type { ExportColumn, ExportDelivery, ExportFormat } from '@/lib/export/types';
import { createLogger } from '@/lib/logger';
import { cn } from '@/lib/utils';

/**
 * Cartão de opção (formato do arquivo / forma de entrega).
 * Substitui o antigo seletor segmentado por uma escolha mais visual: ícone
 * característico, título e uma linha explicando o que acontece.
 */
function OptionCard<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: Array<{
    value: T;
    label: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
    /** Classe de cor do ícone (token semântico) */
    iconClass?: string;
  }>;
  onChange: (valor: T) => void;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {options.map((opcao) => {
        const Icone = opcao.icon;
        const ativo = value === opcao.value;
        return (
          <button
            key={opcao.value}
            type="button"
            onClick={() => onChange(opcao.value)}
            aria-pressed={ativo}
            className={cn(
              'flex items-start gap-2.5 rounded-lg border p-2.5 text-left transition-all',
              ativo
                ? 'border-primary bg-primary/5 ring-1 ring-primary/40'
                : 'border-border/60 bg-background hover:border-border hover:bg-muted/40',
            )}
          >
            <span
              className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                ativo ? 'bg-primary/10' : 'bg-muted',
              )}
            >
              <Icone className={cn('h-4 w-4', opcao.iconClass ?? 'text-muted-foreground')} />
            </span>
            <span className="min-w-0">
              <span
                className={cn(
                  'block text-[13px] font-medium',
                  ativo ? 'text-foreground' : 'text-foreground/90',
                )}
              >
                {opcao.label}
              </span>
              <span className="block text-[11px] leading-snug text-muted-foreground">
                {opcao.description}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}


const logger = createLogger('ExportDialog');

interface ExportDialogProps {
  open: boolean;
  onOpenChange: (aberto: boolean) => void;
  /** Id da fonte registrada (ex.: 'crm_deals') */
  sourceId: string;
  /** Parâmetros da tela (filtros/ordenação) repassados à fonte */
  params: unknown;
  /** Sufixo opcional para o nome do arquivo (ex.: nome da fase) */
  fileNameHint?: string;
}

export function ExportDialog({
  open,
  onOpenChange,
  sourceId,
  params,
  fileNameHint,
}: ExportDialogProps) {
  const { currentTenant, user } = useAuth();
  const { getPermission, loading: carregandoPermissoes } = useMyExportPermissions();
  const { branding } = useSystemSettings();

  /** Cartão de resultado exibido após confirmar a exportação */
  const [resultado, setResultado] = useState<ExportResultState | null>(null);
  /** Arquivo já gerado, mantido em memória para "baixar novamente" */
  const [arquivoGerado, setArquivoGerado] = useState<{ blob: Blob; nome: string } | null>(null);

  const fonte = useMemo(() => getExportSource(sourceId), [sourceId]);
  const permissao = getPermission(sourceId);

  const [formato, setFormato] = useState<ExportFormat>('xls');
  const [entrega, setEntrega] = useState<ExportDelivery>('download');
  const [selecionadas, setSelecionadas] = useState<string[]>([]);
  const [processando, setProcessando] = useState(false);
  /** Busca textual dos campos (apenas visual — não altera a seleção) */
  const [filtro, setFiltro] = useState('');
  /** Total de registros que serão exportados (null = desconhecido) */
  const [totalRegistros, setTotalRegistros] = useState<number | null>(null);
  const [contando, setContando] = useState(false);
  /**
   * v1.7.0: colunas resolvidas pela fonte (campos variáveis por registro).
   * null = a fonte usa a lista fixa declarada em `columns`.
   */
  const [colunasResolvidas, setColunasResolvidas] = useState<ExportColumn[] | null>(null);
  const [carregandoColunas, setCarregandoColunas] = useState(false);

  /** Colunas efetivamente oferecidas no modal */
  const colunasDisponiveis = colunasResolvidas ?? fonte.columns;

  // Colunas agrupadas para exibição, já aplicando a busca
  const grupos = useMemo(() => {
    const termo = filtro.trim().toLowerCase();
    const mapa = new Map<string, ExportColumn[]>();
    for (const coluna of colunasDisponiveis) {
      if (termo && !coluna.label.toLowerCase().includes(termo)) continue;
      const atual = mapa.get(coluna.group) || [];
      mapa.set(coluna.group, [...atual, coluna]);
    }
    return Array.from(mapa.entries());
  }, [colunasDisponiveis, filtro]);


  // Ao abrir: restaura a seleção padrão, a entrega permitida e limpa a busca
  useEffect(() => {
    if (!open) return;
    setColunasResolvidas(null);
    setSelecionadas(fonte.columns.filter((c) => c.defaultSelected).map((c) => c.key));
    setEntrega(permissao.canDownload ? 'download' : 'email');
    setFiltro('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sourceId]);

  // Fontes com campos variáveis: descobre as colunas do registro selecionado
  useEffect(() => {
    if (!open || !currentTenant?.id || !fonte.resolveColumns) return;

    let cancelado = false;
    setCarregandoColunas(true);

    (fonte.resolveColumns as (t: string, p: unknown) => Promise<ExportColumn[]>)(
      currentTenant.id,
      params,
    )
      .then((colunas) => {
        if (cancelado) return;
        setColunasResolvidas(colunas);
        setSelecionadas(colunas.filter((c) => c.defaultSelected).map((c) => c.key));
      })
      .catch((erro) => {
        // Sem as colunas dinâmicas o modal segue com a lista fixa da fonte
        logger.error('Erro ao carregar os campos da exportação:', erro);
      })
      .finally(() => {
        if (!cancelado) setCarregandoColunas(false);
      });

    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sourceId, currentTenant?.id, params]);

  // Conta os registros filtrados assim que o modal abre (informativo)
  useEffect(() => {
    if (!open || !currentTenant?.id || !fonte.countRows) {
      setTotalRegistros(null);
      return;
    }
    let cancelado = false;
    setContando(true);
    setTotalRegistros(null);

    (fonte.countRows as (t: string, p: unknown) => Promise<number>)(currentTenant.id, params)
      .then((total) => {
        if (!cancelado) setTotalRegistros(Number(total) || 0);
      })
      .catch((erro) => {
        // Falha na contagem não impede a exportação — apenas não exibimos o total
        logger.error('Erro ao contar registros da exportação:', erro);
        if (!cancelado) setTotalRegistros(null);
      })
      .finally(() => {
        if (!cancelado) setContando(false);
      });

    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sourceId, currentTenant?.id, params]);

  /** Alterna uma coluna individual */
  const alternarColuna = (key: string) => {
    setSelecionadas((atual) =>
      atual.includes(key) ? atual.filter((k) => k !== key) : [...atual, key],
    );
  };

  /** Marca todas as colunas disponíveis */
  const marcarTodas = () => setSelecionadas(colunasDisponiveis.map((c) => c.key));

  /** Limpa a seleção de colunas */
  const limparSelecao = () => setSelecionadas([]);


  /** Nome base do arquivo */
  const nomeArquivo = () => {
    const sufixo = (fileNameHint || '')
      .replace(/[^a-zA-Z0-9À-ÿ\s-_]/g, '')
      .trim()
      .replace(/\s+/g, '_');
    const data = new Date().toISOString().slice(0, 10);
    return [fonte.fileBaseName, sufixo, data].filter(Boolean).join('_');
  };

  /** Executa a exportação conforme a entrega escolhida */
  const exportar = async () => {
    if (!currentTenant?.id) return;
    if (selecionadas.length === 0) {
      toast.info('Selecione ao menos uma coluna para exportar.');
      return;
    }

    const colunas = colunasDisponiveis.filter((c) => selecionadas.includes(c.key));
    const nomeBase = nomeArquivo();

    // v1.4.0: resumo legível dos filtros/ordenação gravado junto à auditoria.
    // Falha na tradução nunca impede a exportação.
    let filtrosComResumo: Record<string, unknown> = JSON.parse(
      JSON.stringify(params ?? {}),
    ) as Record<string, unknown>;
    if (fonte.describeParams) {
      try {
        const resumo = await (fonte.describeParams as (t: string, p: unknown) => Promise<unknown[]>)(
          currentTenant.id,
          params,
        );
        filtrosComResumo = { ...filtrosComResumo, _resumo: resumo };
      } catch (erro) {
        logger.warn('Falha ao montar resumo legível da exportação:', erro);
      }
    }

    setProcessando(true);
    setArquivoGerado(null);

    // Cartão de progresso (o modal é fechado logo em seguida)
    setResultado({
      status: 'processing',
      fileName: `${nomeBase}.${formato === 'csv' ? 'csv' : 'xlsx'}`,
      format: formato,
      delivery: entrega,
      message:
        entrega === 'email'
          ? 'Gerando o arquivo e preparando o envio...'
          : 'Reunindo os registros filtrados...',
    });
    onOpenChange(false);

    try {
      if (entrega === 'email') {
        // Servidor busca os dados, gera o arquivo e envia por e-mail (com log)
        const { data, error } = await supabase.functions.invoke('export-data-email', {
          body: {
            export_key: sourceId,
            format: formato,
            columns: colunas.map((c) => ({ key: c.key, label: c.label, kind: c.kind || 'text' })),
            params: filtrosComResumo,
            file_name: nomeBase,
            tenant_id: currentTenant.id,
          },
        });
        if (error) {
          // v1.1.1: lê o corpo devolvido pelo servidor para exibir a causa real
          const ctx = (error as { context?: { text?: () => Promise<string> } })?.context;
          const texto = ctx?.text ? await ctx.text() : '';
          let mensagem = error.message;
          try {
            const json = JSON.parse(texto || '{}') as { error?: string };
            if (json?.error) mensagem = json.error;
          } catch {
            if (texto) mensagem = texto;
          }
          throw new Error(mensagem);
        }

        const total = (data as { row_count?: number })?.row_count ?? 0;
        setResultado({
          status: 'success',
          fileName: `${nomeBase}.${formato === 'csv' ? 'csv' : 'xlsx'}`,
          format: formato,
          delivery: 'email',
          rowCount: total,
          message: `Enviado para ${user?.email}`,
        });
        return;
      }

      // v1.6.0: acima de 1.000 registros o arquivo é montado NO SERVIDOR e só
      // devolvido pronto para o navegador (evita o corte da camada de API).
      if ((totalRegistros ?? 0) > LIMITE_NAVEGADOR) {
        const { data: sessao } = await supabase.auth.getSession();
        const token = sessao.session?.access_token;
        if (!token) throw new Error('Sessão expirada. Entre novamente para exportar.');

        const resposta = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/export-data-email`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string,
            },
            body: JSON.stringify({
              export_key: sourceId,
              format: formato,
              delivery: 'download',
              columns: colunas.map((c) => ({
                key: c.key,
                label: c.label,
                kind: c.kind || 'text',
              })),
              params: filtrosComResumo,
              file_name: nomeBase,
              tenant_id: currentTenant.id,
            }),
          },
        );

        if (!resposta.ok) {
          const texto = await resposta.text();
          let mensagem = 'Não foi possível gerar o arquivo.';
          try {
            const json = JSON.parse(texto || '{}') as { error?: string };
            if (json?.error) mensagem = json.error;
          } catch {
            if (texto) mensagem = texto;
          }
          throw new Error(mensagem);
        }

        const blobServidor = await resposta.blob();
        const nomeServidor = `${nomeBase}.${formato === 'csv' ? 'csv' : 'xlsx'}`;
        const totalServidor =
          Number(resposta.headers.get('X-Row-Count')) || (totalRegistros ?? 0);

        triggerDownload(blobServidor, nomeServidor);
        setArquivoGerado({ blob: blobServidor, nome: nomeServidor });

        setResultado({
          status: 'success',
          fileName: nomeServidor,
          format: formato,
          delivery: 'download',
          rowCount: totalServidor,
          message: 'Feito, o arquivo foi baixado com sucesso',
        });
        return;
      }

      // Download direto no navegador (volumes menores)
      const linhas = await (fonte.fetchRows as (t: string, p: unknown) => Promise<Record<string, unknown>[]>)(
        currentTenant.id,
        params,
      );

      if (linhas.length === 0) {
        setResultado(null);
        toast.info('Nenhum registro encontrado com os filtros aplicados.');
        return;
      }

      // Arquivo com cabeçalho de marca (XLSX) ou CSV puro
      const { blob, nomeArquivo: nomeCompleto } = await buildExportBlob({
        formato,
        nomeBase,
        colunas,
        linhas,
        marca: {
          titulo: fonte.title || fonte.label,
          empresa: currentTenant.name || branding.app_name,
          // Prioriza a logomarca cadastrada na página Empresa (tenants.logo_url);
          // só cai para a logo do sistema (SaaS) quando o tenant não tiver uma.
          logoUrl: currentTenant.logo_url || branding.logo_url,
          usuario: user?.email || null,
          nomeAba: fonte.label,
        },
      });

      triggerDownload(blob, nomeCompleto);
      setArquivoGerado({ blob, nome: nomeCompleto });

      // Auditoria da exportação
      await supabase.rpc('log_data_export', {
        p_export_key: sourceId,
        p_format: formato,
        p_delivery: 'download',
        p_row_count: linhas.length,
        p_filters: filtrosComResumo,
        p_columns: colunas.map((c) => c.key),
        p_tenant_hint: currentTenant.id,
      } as never);

      setResultado({
        status: 'success',
        fileName: nomeCompleto,
        format: formato,
        delivery: 'download',
        rowCount: linhas.length,
        message: 'Feito, o arquivo foi baixado com sucesso',
      });
    } catch (erro) {
      logger.error('Erro na exportação:', erro);
      const mensagem = erro instanceof Error ? erro.message : 'Tente novamente.';
      setResultado({
        status: 'error',
        fileName: `${nomeBase}.${formato === 'csv' ? 'csv' : 'xlsx'}`,
        format: formato,
        delivery: entrega,
        message: mensagem,
      });
    } finally {
      setProcessando(false);
    }
  };

  const semPermissao = !carregandoPermissoes && !permissao.canDownload && !permissao.canEmail;
  /** Não há nada para exportar com os filtros atuais */
  const semRegistros = totalRegistros === 0;
  /** Quantidade acima do teto de segurança */
  const acimaDoTeto = (totalRegistros ?? 0) > EXPORT_ROW_LIMIT;

  return (
    <>
    <WorkflowDialogShell
      open={open}
      onOpenChange={onOpenChange}
      icon={Download}
      title="Exportar dados"
      description={`${fonte.label} — respeitando os filtros aplicados na tela`}
      maxWidthClass="sm:max-w-3xl"
      footer={
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={processando}
          >
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={exportar}
            disabled={
              processando || semPermissao || contando || semRegistros || carregandoColunas
            }
          >
            {processando ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : entrega === 'email' ? (
              <Mail className="mr-2 h-4 w-4" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            {entrega === 'email' ? 'Enviar por e-mail' : 'Baixar arquivo'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {semPermissao && (
          <InlineNotice variant="error">
            Seu perfil não possui permissão para exportar dados deste módulo.
          </InlineNotice>
        )}

        {/* ===== Formato e entrega (mesmo cartão) ===== */}
        <SectionCard title="Formato e entrega">
          <div className="grid gap-3">
            <div className="space-y-1.5">
              <CompactLabel help="CSV abre em qualquer planilha; XLS gera uma planilha formatada (.xlsx).">
                Formato do arquivo
              </CompactLabel>
              <OptionCard<ExportFormat>
                value={formato}
                onChange={setFormato}
                options={[
                  {
                    value: 'xls',
                    label: 'Planilha XLS',
                    description: 'Abre no Excel já com formatação e cabeçalho.',
                    icon: FileSpreadsheet,
                    iconClass: 'text-emerald-600',
                  },
                  {
                    value: 'csv',
                    label: 'Arquivo CSV',
                    description: 'Arquivo simples de texto, aceito em qualquer sistema.',
                    icon: FileText,
                    iconClass: 'text-sky-600',
                  },
                ]}
              />
            </div>

            <div className="space-y-1.5">
              <CompactLabel help="As opções disponíveis dependem das permissões de exportação do seu perfil.">
                Como receber
              </CompactLabel>
              <OptionCard<ExportDelivery>
                value={entrega}
                onChange={setEntrega}
                options={[
                  ...(permissao.canDownload
                    ? [
                        {
                          value: 'download' as const,
                          label: 'Baixar arquivo',
                          description: 'O arquivo é gerado e baixado agora no seu computador.',
                          icon: Download,
                          iconClass: 'text-primary',
                        },
                      ]
                    : []),
                  ...(permissao.canEmail
                    ? [
                        {
                          value: 'email' as const,
                          label: 'Enviar por e-mail',
                          description: 'Você recebe o arquivo em anexo no seu e-mail.',
                          icon: Mail,
                          iconClass: 'text-amber-600',
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          </div>

          {/* Volume de registros que será exportado */}
          {contando ? (
            <InlineNotice variant="info">Calculando registros...</InlineNotice>
          ) : semRegistros ? (
            <InlineNotice variant="warning">
              Nenhum registro corresponde aos filtros aplicados na tela.
            </InlineNotice>
          ) : totalRegistros !== null ? (
            <InlineNotice variant={acimaDoTeto ? 'warning' : 'info'}>
              {acimaDoTeto ? (
                <>
                  <strong>{totalRegistros.toLocaleString('pt-BR')}</strong> registros encontrados —
                  apenas os primeiros{' '}
                  <strong>{EXPORT_ROW_LIMIT.toLocaleString('pt-BR')}</strong> serão incluídos no
                  arquivo.
                </>
              ) : (
                <>
                  <strong>{totalRegistros.toLocaleString('pt-BR')}</strong>{' '}
                  {totalRegistros === 1 ? 'registro será exportado' : 'registros serão exportados'},
                  conforme os filtros aplicados na tela.
                  {totalRegistros > LIMITE_NAVEGADOR && (
                    <> O arquivo será preparado no servidor e pode levar alguns instantes.</>
                  )}
                </>
              )}
            </InlineNotice>
          ) : null}

          {entrega === 'email' && (
            <InlineNotice variant="info">
              O arquivo será enviado para <strong>{user?.email}</strong> e o envio ficará
              registrado na auditoria do sistema.
            </InlineNotice>
          )}
        </SectionCard>

        {/* ===== Campos do arquivo ===== */}
        <SectionCard title="Campos do arquivo">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[180px] flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                placeholder="Buscar campo..."
                className="h-8 bg-background pl-8 text-[12px]"
              />
            </div>
            <span className="text-[11px] font-semibold text-muted-foreground">
              {selecionadas.length} de {colunasDisponiveis.length}
            </span>
            <button
              type="button"
              onClick={marcarTodas}
              className="text-[11px] font-semibold text-primary hover:underline"
            >
              Marcar tudo
            </button>
            <span className="text-[11px] text-muted-foreground/50">·</span>
            <button
              type="button"
              onClick={limparSelecao}
              className="text-[11px] font-semibold text-muted-foreground hover:text-foreground hover:underline"
            >
              Limpar
            </button>
          </div>

          {carregandoColunas ? (
            <InlineNotice variant="info">Carregando os campos da lista...</InlineNotice>
          ) : grupos.length === 0 ? (
            <InlineNotice variant="warning">
              Nenhum campo encontrado para "{filtro}".
            </InlineNotice>
          ) : (
            <div className="max-h-[46vh] overflow-y-auto pr-1">
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {grupos.map(([grupo, colunas]) => {
                  const marcadas = colunas.filter((c) => selecionadas.includes(c.key)).length;
                  return (
                    <div
                      key={grupo}
                      className="rounded-lg border border-border/60 bg-background/60 p-2"
                    >
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">
                          {grupo}
                        </span>
                        <span className="text-[10px] font-semibold text-muted-foreground/60">
                          {marcadas}/{colunas.length}
                        </span>
                      </div>
                      <div className="space-y-0.5">
                        {colunas.map((coluna) => {
                          const marcada = selecionadas.includes(coluna.key);
                          return (
                            <label
                              key={coluna.key}
                              className={cn(
                                'flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1',
                                'text-[12px] leading-tight transition-colors hover:bg-muted/60',
                                marcada ? 'text-foreground' : 'text-muted-foreground',
                              )}
                            >
                              <Checkbox
                                className="h-3.5 w-3.5"
                                checked={marcada}
                                onCheckedChange={() => alternarColuna(coluna.key)}
                              />
                              <span className="truncate">{coluna.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </SectionCard>
      </div>

    </WorkflowDialogShell>

    {/* Cartão de resultado (progresso / sucesso / erro) */}
    <ExportResultToast
      state={resultado}
      onClose={() => setResultado(null)}
      onRedownload={
        arquivoGerado ? () => triggerDownload(arquivoGerado.blob, arquivoGerado.nome) : undefined
      }
      onRetry={() => {
        setResultado(null);
        onOpenChange(true);
      }}
    />
    </>
  );
}
